import {
  Avatar,
  Box,
  Chip,
  LinearProgress,
  Stack,
  TableCell,
  TableRow,
  Typography
} from '@mui/material';
import { differenceInCalendarDays, parse } from 'date-fns';
import EllipsisCellWithTooltip from 'src/components/table/ellipsis-cell-with-tooltip';
import { fDate } from 'src/utils/format-time';

const getProgressColor = (progress) => {
  if (progress >= 88) return '#22c55e';
  if (progress >= 60) return '#3b82f6';
  if (progress >= 35) return '#f59e0b';
  return '#94a3b8';
};

const TableLinkRow = ({ row, serialNumber }) => {

  const today = new Date();

  const completionDate = new Date(row.estimateCompletion);

  const daysUntil = differenceInCalendarDays(completionDate, today);

  const isOverdue = daysUntil < 0;

  const dateLabel = isOverdue
    ? `${Math.abs(daysUntil)}d overdue`
    : daysUntil === 0
      ? 'Today'
      : daysUntil === 1
        ? 'Tomorrow'
        : fDate(row.estimateCompletion);

  const hasUnread = row.unreadMessageCount;

  const currentBuildStage = row?.buildStage?.find((stage) => stage.status === "current");

  return (
    <TableRow
      hover
      tabIndex={-1}
      sx={{
        ...(isOverdue && {
          bgcolor: '#fef2f2',
          borderLeft: '3px solid #ef4444',
          '&:hover': { bgcolor: '#fee2e2' },
        }),
      }}
    >
      {/* # */}
      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.disabled', fontSize: 13 }}>
        {serialNumber}
      </TableCell>

      {/* Contractor */}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* <Avatar alt={row.name} src={row.image} sx={{ width: 34, height: 34 }} /> */}
          <Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography fontSize={13} fontWeight={500} sx={{
                minWidth: 200,
                maxWidth: 200,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}>
                {row.contractor?.map((contractor) => contractor.name).join(', ')}
              </Typography>
              {isOverdue && (
                <Chip
                  label="overdue"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 600,
                    bgcolor: '#ef4444',
                    color: '#fff',
                    borderRadius: '4px',
                    '& .MuiChip-label': { px: '6px' },
                    '&:hover': { bgcolor: '#ef4444' },
                    pointerEvents: 'none',
                  }}
                />
              )}
            </Stack>
            <Typography fontSize={11} color="text.disabled">
              {row.email}
            </Typography>
          </Stack>
        </Stack>
      </TableCell>
      <TableCell>
        {row?.name || '--'}
      </TableCell>


      {/* Stage + progress */}
      <TableCell sx={{ minWidth: 140 }}>
        <Stack direction="row" justifyContent="space-between" mb={0.5}>
          <Typography fontSize={12} color="text.secondary">
            {currentBuildStage?.name || '--'}
          </Typography>
          <Typography fontSize={11} color="text.disabled">
            {row?.progress ?? 0}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={row?.progress || 0}
          sx={{
            height: 5,
            borderRadius: 3,
            bgcolor: '#E5E7EB',
            '& .MuiLinearProgress-bar': {
              bgcolor: getProgressColor(row?.progress || 0),
              borderRadius: 3,
            },
          }}
        />
      </TableCell>

      {/* Sales leads */}
      <EllipsisCellWithTooltip maxWidth="160px">
        {row?.salesLeads?.map((lead) => lead?.name)?.join(', ') || '--'}
      </EllipsisCellWithTooltip>

      {/* Build leads */}
      <EllipsisCellWithTooltip maxWidth="160px">
        {row?.buildLeads.map((lead) => lead?.name)?.join(', ') ?? '--'}
      </EllipsisCellWithTooltip>

      {/* Estimated completion */}
      <TableCell
        align="center"
        sx={{
          whiteSpace: 'nowrap',
          fontSize: 12,
          fontWeight: isOverdue ? 600 : 400,
          color: isOverdue ? '#ef4444' : 'text.secondary',
        }}
      >
        {dateLabel}
      </TableCell>

      {/* Unread messages flag */}
      <TableCell align="center">
        {hasUnread ? (
          <Chip
            icon={
              <Box
                component="span"
                sx={{ fontSize: 13, lineHeight: 1, ml: '6px !important' }}
              >
              💬 
              <Typography component="span" sx={{ ml: 0.5, fontSize: 12, color: '#fff' }}>
                 {row.unreadMessageCount}
              </Typography>
              </Box>
            }

            label={row.messages}
            size="small"
            sx={{
              bgcolor: '#ef4444',
              color: '#fff',
              fontWeight: 700,
              fontSize: 12,
              height: 26,
              borderRadius: '6px',
              animation: 'flagPulse 1.8s ease-in-out infinite',
              '& .MuiChip-label': { pr: '8px' },
              '&:hover': { bgcolor: '#ef4444' },
              pointerEvents: 'none',
              '@keyframes flagPulse': {
                '0%,100%': { boxShadow: '0 0 0 0 rgba(239,68,68,0.5)' },
                '50%': { boxShadow: '0 0 0 5px rgba(239,68,68,0)' },
              },
            }}
          />
        ) : (
          <Typography fontSize={12} color="text.disabled">
            —
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

export default TableLinkRow;